import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLOpenGraph = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">SEO & Discovery</div>
                <h1>Open Graph & Social Media Meta Tags</h1>
                <p className="hero-lead">
                    Control how your content appears when shared on social media. Learn Open Graph,
                    Twitter Cards, and other social meta tags for beautiful, engaging social shares.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Social Meta Tags Matter</h2>
                <p>
                    When someone shares your link on Facebook, Twitter, LinkedIn, or other platforms,
                    social meta tags control the title, description, image, and other display elements.
                </p>

                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3> Higher CTR</h3>
                        <p>Rich previews get 3-5x more clicks</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Brand Control</h3>
                        <p>Control how your brand appears</p>
                    </div>
                    <div className="benefit-card">
                        <h3>More Shares</h3>
                        <p>Attractive previews encourage sharing</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Better Tracking</h3>
                        <p>Track social media traffic sources</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Open Graph Protocol (Facebook, LinkedIn)</h2>
                <p>
                    Open Graph was created by Facebook and is now used by most social platforms including
                    LinkedIn, Pinterest, and Slack.
                </p>

                <h3>Basic Open Graph Tags</h3>
                <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <!-- Essential Open Graph tags --&gt;
    <meta property="og:title" content="Free Image Compressor - Reduce JPG, PNG Size">
    <meta property="og:description" content="Compress images online for free. Reduce file sizes by up to 80% without losing quality. Fast, secure, no registration.">
    <meta property="og:image" content="https://www.filefusion.app/images/og-image-compressor.jpg">
    <meta property="og:url" content="https://www.filefusion.app/tools/image-compressor">
    <meta property="og:type" content="website">
    
    <!-- Optional but recommended --&gt;
    <meta property="og:site_name" content="FileFusion">
    <meta property="og:locale" content="en_US">
</head>
<body>
    <!-- Content --&gt;
</body>
</html>`} />

                <h3>Open Graph Tag Reference</h3>
                <table className="reference-table">
                    <thead>
                        <tr>
                            <th>Tag</th>
                            <th>Description</th>
                            <th>Required</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>og:title</code></td>
                            <td>Title of your content (60-90 chars)</td>
                            <td>Yes</td>
                        </tr>
                        <tr>
                            <td><code>og:description</code></td>
                            <td>Description (155-200 chars)</td>
                            <td>Yes</td>
                        </tr>
                        <tr>
                            <td><code>og:image</code></td>
                            <td>Image URL (1200x630px optimal)</td>
                            <td>Yes</td>
                        </tr>
                        <tr>
                            <td><code>og:url</code></td>
                            <td>Canonical URL of page</td>
                            <td>Yes</td>
                        </tr>
                        <tr>
                            <td><code>og:type</code></td>
                            <td>Content type (website, article, etc.)</td>
                            <td>Yes</td>
                        </tr>
                        <tr>
                            <td><code>og:site_name</code></td>
                            <td>Name of website</td>
                            <td>Recommended</td>
                        </tr>
                        <tr>
                            <td><code>og:locale</code></td>
                            <td>Language/locale (en_US, es_ES)</td>
                            <td>Optional</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="content-section">
                <h2>Open Graph Image Best Practices</h2>

                <CodeBlock language="html" code={`<!-- Optimal image specifications --&gt;
<meta property="og:image" content="https://www.filefusion.app/images/og-image.jpg">
<meta property="og:image:width" content="1200">
<meta property="og:image:height" content="630">
<meta property="og:image:alt" content="FileFusion - Free Online File Tools">
<meta property="og:image:type" content="image/jpeg">

<!-- Multiple images (platforms pick best) --&gt;
<meta property="og:image" content="https://www.filefusion.app/images/og-large.jpg">
<meta property="og:image" content="https://www.filefusion.app/images/og-square.jpg">`} />

                <h3>Image Requirements:</h3>
                <ul>
                    <li> <strong>Size:</strong> 1200x630px (1.91:1 ratio) recommended</li>
                    <li> <strong>Minimum:</strong> 600x315px</li>
                    <li> <strong>File size:</strong> Less than 8MB</li>
                    <li> <strong>Format:</strong> JPG or PNG (no transparency on Facebook)</li>
                    <li> <strong>Safe zone:</strong> Keep important elements in center 1:1 square</li>
                    <li> <strong>Text:</strong> Minimal text (20% rule no longer enforced but recommended)</li>
                    <li> <strong>HTTPS:</strong> Must be served over HTTPS</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Twitter Cards</h2>
                <p>
                    Twitter uses its own meta tags but falls back to Open Graph if Twitter tags aren't present.
                </p>

                <h3>Summary Card with Large Image</h3>
                <CodeBlock language="html" code={`<head>
    <!-- Twitter Card tags --&gt;
    <meta name="twitter:card" content="summary_large_image">
    <meta name="twitter:site" content="@filefusion">
    <meta name="twitter:creator" content="@johndoe">
    <meta name="twitter:title" content="Free Image Compressor - Reduce JPG, PNG Size">
    <meta name="twitter:description" content="Compress images online for free. Reduce file sizes by up to 80% without losing quality.">
    <meta name="twitter:image" content="https://www.filefusion.app/images/twitter-card.jpg">
    <meta name="twitter:image:alt" content="Image compression comparison showing before and after">
    
    <!-- Open Graph (Twitter uses as fallback) --&gt;
    <meta property="og:title" content="Free Image Compressor - Reduce JPG, PNG Size">
    <meta property="og:description" content="Compress images online for free. Reduce file sizes by up to 80% without losing quality.">
    <meta property="og:image" content="https://www.filefusion.app/images/og-image.jpg">
    <meta property="og:url" content="https://www.filefusion.app/tools/image-compressor">
</head>`} />

                <h3>Twitter Card Types</h3>
                <ul>
                    <li><strong>summary:</strong> Small image (1:1 ratio, 144x144px min)</li>
                    <li><strong>summary_large_image:</strong> Large image (2:1 ratio, 300x157px min, 4096x4096px max)</li>
                    <li><strong>app:</strong> Mobile app download</li>
                    <li><strong>player:</strong> Video/audio player</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Article-Specific Tags</h2>

                <h3>For Blog Posts and Tutorials</h3>
                <CodeBlock language="html" code={`<head>
    <!-- Article type --&gt;
    <meta property="og:type" content="article">
    
    <!-- Article metadata --&gt;
    <meta property="article:published_time" content="2024-01-15T08:00:00+00:00">
    <meta property="article:modified_time" content="2024-01-20T10:30:00+00:00">
    <meta property="article:author" content="https://www.filefusion.app/authors/jane-smith">
    <meta property="article:section" content="Tutorials">
    <meta property="article:tag" content="Image Compression">
    <meta property="article:tag" content="Web Performance">
    <meta property="article:tag" content="SEO">
    
    <!-- Publisher info --&gt;
    <meta property="og:site_name" content="FileFusion">
    
    <!-- Author social profiles --&gt;
    <meta name="twitter:creator" content="@janesmith">
</head>`} />
            </div>

            <div className="content-section">
                <h2>Video Meta Tags</h2>

                <CodeBlock language="html" code={`<head>
    <!-- Video type --&gt;
    <meta property="og:type" content="video.other">
    
    <!-- Video URL --&gt;
    <meta property="og:video" content="https://www.filefusion.app/videos/tutorial.mp4">
    <meta property="og:video:secure_url" content="https://www.filefusion.app/videos/tutorial.mp4">
    <meta property="og:video:type" content="video/mp4">
    <meta property="og:video:width" content="1920">
    <meta property="og:video:height" content="1080">
    
    <!-- Video thumbnail --&gt;
    <meta property="og:image" content="https://www.filefusion.app/videos/thumbnail.jpg">
    
    <!-- Twitter player card --&gt;
    <meta name="twitter:card" content="player">
    <meta name="twitter:player" content="https://www.filefusion.app/videos/player.html">
    <meta name="twitter:player:width" content="1280">
    <meta name="twitter:player:height" content="720">
</head>`} />
            </div>

            <div className="content-section">
                <h2>Complete Social Media Template</h2>

                <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    
    <!-- Basic SEO --&gt;
    <title>Free Image Compressor - Reduce JPG, PNG Size | FileFusion</title>
    <meta name="description" content="Compress images online for free. Reduce file sizes by up to 80% without losing quality. Fast, secure, no registration.">
    <link rel="canonical" href="https://www.filefusion.app/tools/image-compressor">
    
    <!-- Open Graph / Facebook --&gt;
    <meta property="og:type" content="website">
    <meta property="og:url" content="https://www.filefusion.app/tools/image-compressor">
    <meta property="og:title" content="Free Image Compressor - Reduce JPG, PNG Size">
    <meta property="og:description" content="Compress images online for free. Reduce file sizes by up to 80% without losing quality. Fast, secure, no registration.">
    <meta property="og:image" content="https://www.filefusion.app/images/og-compressor.jpg">
    <meta property="og:image:width" content="1200">
    <meta property="og:image:height" content="630">
    <meta property="og:image:alt" content="FileFusion Image Compressor Tool">
    <meta property="og:site_name" content="FileFusion">
    <meta property="og:locale" content="en_US">
    
    <!-- Twitter --&gt;
    <meta name="twitter:card" content="summary_large_image">
    <meta name="twitter:site" content="@filefusion">
    <meta name="twitter:creator" content="@filefusion">
    <meta name="twitter:title" content="Free Image Compressor - Reduce JPG, PNG Size">
    <meta name="twitter:description" content="Compress images online for free. Reduce file sizes by up to 80% without losing quality.">
    <meta name="twitter:image" content="https://www.filefusion.app/images/twitter-compressor.jpg">
    <meta name="twitter:image:alt" content="Image compression tool interface">
    
    <!-- Additional platforms --&gt;
    <meta property="fb:app_id" content="your-facebook-app-id">
    <meta name="pinterest-rich-pin" content="true">
    
    <!-- Favicon --&gt;
    <link rel="icon" type="image/png" href="/favicon.png">
    <link rel="apple-touch-icon" href="/apple-touch-icon.png">
</head>
<body>
    <!-- Content --&gt;
</body>
</html>`} />
            </div>

            <div className="content-section">
                <h2>Platform-Specific Considerations</h2>

                <h3>Facebook</h3>
                <ul>
                    <li> Image: 1200x630px optimal</li>
                    <li> Cache: Facebook caches for 7 days (use Sharing Debugger to refresh)</li>
                    <li> App ID: Add <code>fb:app_id</code> for Facebook Insights</li>
                    <li> Text: Keep title under 60 characters, description under 200</li>
                </ul>

                <h3>Twitter</h3>
                <ul>
                    <li> Large image: 2:1 ratio (800x418px min)</li>
                    <li> Summary: 1:1 ratio (144x144px min)</li>
                    <li> Username: Use <code>@username</code> format</li>
                    <li> Text: Title 70 chars max, description 200 chars max</li>
                </ul>

                <h3>LinkedIn</h3>
                <ul>
                    <li> Image: 1200x627px optimal</li>
                    <li> Uses Open Graph tags</li>
                    <li> Title: 150 characters max</li>
                    <li> Description: 256 characters max</li>
                </ul>

                <h3>Pinterest</h3>
                <ul>
                    <li> Image: 1000x1500px optimal (2:3 ratio)</li>
                    <li> Vertical images perform best</li>
                    <li> Add <code>meta name="pinterest-rich-pin"</code></li>
                </ul>

                <h3>WhatsApp</h3>
                <ul>
                    <li> Uses Open Graph tags</li>
                    <li> Image: 300x300px min</li>
                    <li>Shows title, description, and thumbnail</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Testing Social Meta Tags</h2>

                <h3>Testing Tools</h3>
                <div className="tools-list">
                    <ul>
                        <li>
                            <strong>Facebook Sharing Debugger:</strong>
                            <br />
                            <code>https://developers.facebook.com/tools/debug/</code>
                            <br />
                            <em>Test and refresh Facebook cache</em>
                        </li>
                        <li>
                            <strong>Twitter Card Validator:</strong>
                            <br />
                            <code>https://cards-dev.twitter.com/validator</code>
                            <br />
                            <em>Preview Twitter cards</em>
                        </li>
                        <li>
                            <strong>LinkedIn Post Inspector:</strong>
                            <br />
                            <code>https://www.linkedin.com/post-inspector/</code>
                            <br />
                            <em>Test LinkedIn previews</em>
                        </li>
                        <li>
                            <strong>Open Graph Checker:</strong>
                            <br />
                            <code>https://www.opengraph.xyz/</code>
                            <br />
                            <em>Test multiple platforms at once</em>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Common Mistakes</h2>

                <div className="mistakes-list">
                    <h3> Mistake 1: Using Relative URLs</h3>
                    <CodeBlock className="error" language="html" code={`<!--  Wrong --&gt;
<meta property="og:image" content="/images/og-image.jpg">

<!-- Correct --&gt;
<meta property="og:image" content="https://www.filefusion.app/images/og-image.jpg">`} />

                    <h3> Mistake 2: Wrong Image Size</h3>
                    <CodeBlock className="error" language="html" code={`<!--  Too small (will be blurry) --&gt;
<meta property="og:image" content="...logo-200x200.png">

<!-- Optimal size --&gt;
<meta property="og:image" content="...og-image-1200x630.jpg">`} />

                    <h3> Mistake 3: No Image Alt Text</h3>
                    <CodeBlock className="error" language="html" code={`<!--  Missing alt --&gt;
<meta property="og:image" content="...image.jpg">

<!-- With alt text --&gt;
<meta property="og:image" content="...image.jpg">
<meta property="og:image:alt" content="Description of image">`} />

                    <h3> Mistake 4: Same Tags for All Pages</h3>
                    <CodeBlock className="error" language="html" code={`<!--  Generic title for all pages --&gt;
<meta property="og:title" content="FileFusion">

<!-- Page-specific title --&gt;
<meta property="og:title" content="Image Compressor - FileFusion">`} />

                    <h3> Mistake 5: Not Testing</h3>
                    <p>Always test your social tags before sharing!</p>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices Checklist</h2>
                <div className="checklist">
                    <ul>
                        <li> Use absolute URLs (not relative)</li>
                        <li> Optimize images (1200x630px for Facebook/LinkedIn)</li>
                        <li> Include Twitter Card tags</li>
                        <li> Add image alt text (og:image:alt)</li>
                        <li> Keep titles under 60 characters</li>
                        <li> Keep descriptions under 200 characters</li>
                        <li> Use HTTPS for all URLs</li>
                        <li> Unique tags for each page</li>
                        <li> Test with platform-specific tools</li>
                        <li> Include og:type (website, article, etc.)</li>
                        <li> Add fb:app_id for Facebook Insights</li>
                        <li> Use meaningful, descriptive text</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLOpenGraph;




